import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Auth Service
 * Handles all authentication-related API calls
 */
const authService = {
    /**
     * Login user with email and password
     * @param {string} email - User email
     * @param {string} password - User password
     * @returns {Promise} API response with token
     */
    login: async (email, password) => {
        try {
            const response = await axios.post(`${API_URL}/api/v1/auth/login`, {
                email,
                password
            });
            
            return {
                success: true,
                data: response.data
            };
        } catch (error) {
            // Handle error responses from API
            if (error.response) {
                return {
                    success: false,
                    message: error.response.data.message || 'Login failed',
                    status: error.response.status
                };
            }
            
            // Handle network errors
            return {
                success: false,
                message: 'Network error. Please check your connection.',
                status: null
            };
        }
    },

    /**
     * Logout user (clear local storage)
     */
    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    },

    /**
     * Get stored token from localStorage
     * @returns {string|null} JWT token or null
     */
    getToken: () => {
        return localStorage.getItem('token');
    },

    /**
     * Store token in localStorage
     * @param {string} token - JWT token
     */
    setToken: (token) => {
        localStorage.setItem('token', token);
    },

    /**
     * Check if token exists and is not expired
     * @returns {boolean} True if token is valid
     */
    isTokenValid: () => {
        const token = authService.getToken();
        if (!token) return false;

        try {
            // Decode token to check expiration
            const payload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            
            return payload.exp > currentTime;
        } catch (error) {
            return false;
        }
    },

    /**
     * Decode JWT token and return payload
     * @returns {object|null} Token payload or null
     */
    decodeToken: () => {
        const token = authService.getToken();
        if (!token) return null;

        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload;
        } catch (error) {
            console.error('Error decoding token:', error);
            return null;
        }
    },

    /**
     * Register new user
     * - For FREE_TIER: Creates account immediately and returns JWT token
     * - For paid tiers: Initiates Stripe checkout and returns checkout URL
     * @param {string} email - User email
     * @param {string} password - User password
     * @param {string} subscriptionTier - Subscription tier (FREE_TIER, BASIC, PRO, ELITE, etc.)
     * @param {string} sport - Sport category (NBA, AFL) - Optional for FREE_TIER
     * @returns {Promise} API response with token (FREE_TIER) or checkout URL (paid tiers)
     */
    register: async (email, password, subscriptionTier, sport = null) => {
        try {
            // Build request body - only include sport for paid tiers
            const requestBody = {
                email,
                password,
                subscription_tier: subscriptionTier
            };
            
            // Only add sport if provided (required for paid tiers, optional for FREE_TIER)
            if (sport) {
                requestBody.sport = sport;
            }
            
            const response = await axios.post(`${API_URL}/api/v1/auth/register`, requestBody);
            
            // FREE_TIER returns token directly, paid tiers return checkout data
            if (subscriptionTier === 'FREE_TIER' && response.data.token) {
                return {
                    success: true,
                    token: response.data.token,
                    user: response.data.user,
                    message: response.data.message
                };
            }
            
            return {
                success: true,
                data: response.data.data,
                message: response.data.message
            };
        } catch (error) {
            console.error('Registration API error:', error);
            
            // Handle error responses from API
            if (error.response) {
                return {
                    success: false,
                    message: error.response.data.message || 'Registration failed',
                    error: error.response.data.error,
                    status: error.response.status
                };
            }
            
            // Handle network errors
            return {
                success: false,
                message: 'Network error. Please check your connection.',
                status: null
            };
        }
    },

    /**
     * Get registration info from Stripe session ID
     * @param {string} sessionId - Stripe checkout session ID
     * @returns {Promise} API response with registration info
     */
    getRegistrationInfo: async (sessionId) => {
        try {
            const response = await axios.get(`${API_URL}/api/v1/auth/registration-info/${sessionId}`);
            
            return {
                success: true,
                data: response.data.data
            };
        } catch (error) {
            console.error('Get registration info API error:', error);
            
            // Handle error responses from API
            if (error.response) {
                return {
                    success: false,
                    message: error.response.data.message || 'Failed to retrieve registration info',
                    status: error.response.status
                };
            }
            
            // Handle network errors
            return {
                success: false,
                message: 'Network error. Please check your connection.',
                status: null
            };
        }
    }
};

export default authService;

